import { Injectable, inject } from '@angular/core';
import {
  ActionSheetController,
  AlertController,
  Platform,
  ToastController,
} from '@ionic/angular';
import { TranslateService } from '@ngx-translate/core';
import { lastValueFrom } from 'rxjs';

export enum ALERT_TYPE {
  ERROR,
  SUCCESS,
  CAUTION,
}

export interface MODAL_ALERT_BUTTON {
  text: string;
  data: {
  action: string | any;
  }
  role?: string;
}

export interface MODAL_ALERT_SHEET_BUTTONS {
  text: string;
  handler?: () => void | Promise<void>;
  role?: string;
}

/**
 * Serviço que mostra todos os tipos de alertas disponívels na aplicação.
 */
@Injectable({ providedIn: 'root' })
export class AlertService {
  private toastCtrl = inject(ToastController);
  private platform = inject(Platform);
  private translateService = inject(TranslateService);
  private actionSheetCtrl = inject(ActionSheetController);
  private alertCtrl = inject(AlertController);

  /**
   * Mostra alerta para usuário. O tipo de alerta é definido através
   * do parâmetro 'alertType'.
   * @param {ALERT_TYPE} alertType enum com os tipos de alertas
   * @param {string} message mensagem a ser postada para o cliente deve
   * ser passada ja utilizando a tag do 'Translate Module'. Por exemplo:
   * 'MESSAGE:badLogin'. Este exemplo já deve vir do translations.
   * @param {number} duration opcional, é automaticamente de 5 segundos.
   */
  async showAlert(
    alertType: ALERT_TYPE,
    message: string,
    duration: number = 5000,
    position: 'top' | 'bottom' = 'top'
  ): Promise<void> {
    const isDesktop = this.platform.is('desktop');
    /** handles translation */
    await lastValueFrom(this.translateService.get('COMMON.ALERTS.badLogin'));
    message = this.translateService.instant(message);

    let icon;
    const cssClass = [isDesktop ? '' : 'toast-mobile'];

    switch (alertType) {
      case ALERT_TYPE.CAUTION:
        icon = 'alert-circle-outline';
        cssClass.push('caution');
        break;

      case ALERT_TYPE.ERROR:
        icon = 'close-circle-outline';
        cssClass.push('error');
        break;

      case ALERT_TYPE.SUCCESS:
        icon = 'checkmark-circle-outline';
        cssClass.push('success');
        break;
    }
    const toast = await this.toastCtrl.create({
      icon,
      message,
      duration,
      cssClass,
      position,
      swipeGesture: 'vertical',
    });
    await toast.present();
  }

    async showActionSheetAlert(
      title: string,
      message: string,
      buttons: MODAL_ALERT_SHEET_BUTTONS[]
    ): Promise<void> {
      const aS = await this.actionSheetCtrl.create({
        buttons,
        header: this.translateService.instant(title),
        subHeader: this.translateService.instant(message)
      });
      await aS.present();
    }

  async showWindowAlert(
    title: string,
    message: string,
    showCancel = true
  ): Promise<boolean> {
    return new Promise(async (resolve) => {
      const buttons = [
        {
          text: 'OK',
          role: 'confirm',
          handler: () => {
            resolve(true);
          },
        },
      ];
      if (showCancel) buttons.push({
        text: this.translateService.instant('COMMON.cancel'),
        role: 'cancel',
        handler: () => {
          resolve(false);
        },
      },)
      const alert = await this.alertCtrl.create({
        header: title,
        message,
        buttons,
      });
      await alert.present();
    });
  }
}
